import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { FormularioCampo } from '@/types/formulario';
import { toast } from 'sonner';

export function useFormularioCampos(formularioId?: string) {
  const queryClient = useQueryClient();

  const { data: campos = [], isLoading } = useQuery({
    queryKey: ['formulario_campos', formularioId],
    queryFn: async () => {
      if (!formularioId) return [];
      
      const { data, error } = await supabase
        .from('formulario_campos')
        .select('*')
        .eq('formulario_id', formularioId)
        .eq('ativo', true)
        .order('ordem', { ascending: true });

      if (error) throw error;
      return data as FormularioCampo[];
    },
    enabled: !!formularioId,
  });

  const addCampo = useMutation({
    mutationFn: async (campo: Omit<FormularioCampo, 'id' | 'criado_em' | 'atualizado_em'>) => {
      const { data, error } = await supabase
        .from('formulario_campos')
        .insert(campo)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['formulario_campos', formularioId] });
      toast.success('Campo adicionado com sucesso');
    },
    onError: (error) => {
      toast.error('Erro ao adicionar campo: ' + error.message);
    },
  });

  const updateCampo = useMutation({
    mutationFn: async ({ id, ...updates }: Partial<FormularioCampo> & { id: string }) => {
      const { data, error } = await supabase
        .from('formulario_campos')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['formulario_campos', formularioId] });
      toast.success('Campo atualizado com sucesso');
    },
    onError: (error) => {
      toast.error('Erro ao atualizar campo: ' + error.message);
    },
  });

  const deleteCampo = useMutation({
    mutationFn: async (campoId: string) => {
      const { error } = await supabase
        .from('formulario_campos')
        .update({ ativo: false })
        .eq('id', campoId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['formulario_campos', formularioId] });
      toast.success('Campo removido com sucesso');
    },
    onError: (error) => {
      toast.error('Erro ao remover campo: ' + error.message);
    },
  });

  const reorderCampos = useMutation({
    mutationFn: async (reorderedCampos: { id: string; ordem: number }[]) => {
      const updates = reorderedCampos.map(({ id, ordem }) =>
        supabase.from('formulario_campos').update({ ordem }).eq('id', id)
      );

      await Promise.all(updates);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['formulario_campos', formularioId] });
      toast.success('Ordem dos campos atualizada');
    },
    onError: (error) => {
      toast.error('Erro ao reordenar campos: ' + error.message);
    },
  });

  return {
    campos,
    isLoading,
    addCampo: addCampo.mutate,
    updateCampo: updateCampo.mutate,
    deleteCampo: deleteCampo.mutate,
    reorderCampos: reorderCampos.mutate,
    isAdding: addCampo.isPending,
    isUpdating: updateCampo.isPending,
  };
}
